import _sortBy from 'lodash/sortBy';
import { GL300_REGEX, GL500_REGEX } from 'src/services/constants';
import {
  devicesMatchingTerms,
  isAssetDevice,
  isDashcamDevice,
  isDirectConnectDevice,
  isObdDevice,
  isOtherDeviceType,
} from 'src/services/device';

/**
 * Returns all asset (non-vehicle) devices.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 */
export function assetDevices(_, getters) {
  return getters.devices.filter(isAssetDevice);
}

/**
 * Returns all dashcam/Raven devices.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 */
export function dashcamDevices(_, getters) {
  return getters.devices.filter(isDashcamDevice);
}

/**
 * Returns all dashcam/Raven devices.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 */
export function directConnectDevices(_, getters) {
  return getters.devices.filter(isDirectConnectDevice);
}

/**
 * Returns all devices that do not fit under another category.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 */
export function otherDevices(_, getters) {
  return getters.devices.filter(isOtherDeviceType);
}

/**
 * Returns all asset (non-vehicle) devices that match the current search filter.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 * @param {import('src/types/store').RootStoreState} rootState
 * @param {import('src/types/store').RootStoreGetters} rootGetters
 */
export function filteredAssetDevices(_, getters, __, rootGetters) {
  return devicesMatchingTerms(getters.assetDevices, rootGetters['search/processedFilterTerms']);
}

/**
 * Returns all Dashcam devices that match the current search filter.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 * @param {import('src/types/store').RootStoreState} rootState
 * @param {import('src/types/store').RootStoreGetters} rootGetters
 */
export function filteredDashcamDevices(_, getters, __, rootGetters) {
  return devicesMatchingTerms(getters.dashcamDevices, rootGetters['search/processedFilterTerms']);
}

/**
 * Returns all Direct Connect devices that match the current search filter.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 * @param {import('src/types/store').RootStoreState} rootState
 * @param {import('src/types/store').RootStoreGetters} rootGetters
 */
export function filteredDirectConnectDevices(_, getters, __, rootGetters) {
  return devicesMatchingTerms(getters.directConnectDevices, rootGetters['search/processedFilterTerms']);
}

/**
 * Returns all OBDII devices that match the current search filter.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 * @param {import('src/types/store').RootStoreState} rootState
 * @param {import('src/types/store').RootStoreGetters} rootGetters
 */
export function filteredObd2Devices(_, getters, __, rootGetters) {
  return devicesMatchingTerms(getters.obd2Devices, rootGetters['search/processedFilterTerms']);
}

/**
 * Returns all asset (non-vehicle) devices that match the current search filter.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 * @param {import('src/types/store').RootStoreState} rootState
 * @param {import('src/types/store').RootStoreGetters} rootGetters
 */
export function filteredOtherDevices(_, getters, __, rootGetters) {
  return devicesMatchingTerms(getters.otherDevices, rootGetters['search/processedFilterTerms']);
}

/**
 * Returns all OBDII devices.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 */
export function obd2Devices(_, getters) {
  return getters.devices.filter(isObdDevice);
}

/**
 * Returns all devices as an array, sorted by serial, with asset info included.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 * @param {import('src/types/store').RootStoreState} rootState
 * @param {import('src/types/store').RootStoreGetters} rootGetters
 */
export function devices(state, _, __, rootGetters) {
  /** @type {Object.<string, import('src/models/Asset').default|import('src/models/Vehicle').default>} */
  const assetsByDeviceKey = rootGetters['assets/assetsByDeviceKey'];
  const devs = Object.values(state.devices);
  return _sortBy(devs, 'serial').map((device) => {
    const asset = assetsByDeviceKey[device.key];
    const { serial } = device;
    return {
      ...device,
      asset,
      assetName: asset?.nickname || '',
      obsolete: GL300_REGEX.test(serial) || GL500_REGEX.test(serial),
    };
  });
}

/**
 * Returns all devices by key.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @returns {{ [key: string]: import('src/models/Device').default }}
 */
export function devicesByKey(state) {
  const devices = {};
  Object.values(state.devices).forEach((device) => {
    devices[device.key] = device;
  });
  return devices;
}

/**
 * Returns true if devices are loading.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 */
export function devicesLoading(state) {
  return Boolean(state.devicesPromise);
}

/**
 * Returns all devices that are permitted to go through the pairing flow.
 *
 * @param {import('src/types/devices-store').DevicesStoreState} state
 * @param {import('src/types/devices-store').DevicesStoreGetters} getters
 */
export function pairableDevices(_, getters) {
  return [...getters.assetDevices, ...getters.otherDevices];
}
